import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { supabase } from '../lib/supabase';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Plyr from 'plyr-react';
import "plyr-react/plyr.css";

interface ImageCache {
  [key: string]: {
    aspectRatio: number;
    loaded: boolean;
  };
}

export function ShareProject() {
  const [searchParams] = useSearchParams();
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageCache, setImageCache] = useState<ImageCache>({});

  useEffect(() => {
    loadProject();
  }, []);

  const loadProject = async () => {
    const projectId = searchParams.get('projectId');
    if (!projectId) {
      setError('No project ID provided');
      setLoading(false);
      return;
    }

    try {
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .select(`
          id,
          project_name,
          description,
          customer:customers (
            name
          ),
          images:project_images (
            id,
            url,
            video_url,
            media_type,
            display_order,
            created_at
          )
        `)
        .eq('id', projectId)
        .single();

      if (projectError) throw projectError;
      if (!projectData) throw new Error('Project not found');

      setProject(projectData);

      // Preload images
      projectData.images.forEach((image: any, index: number) => {
        if (image.media_type === 'image') {
          preloadImage(image.url, index);
        }
      });
    } catch (err) {
      console.error('Error loading project:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while loading the project');
    } finally {
      setLoading(false);
    }
  };

  const getYouTubeThumbnail = (url: string) => {
    const videoId = url.match(/embed\/([^?]+)/)?.[1];
    return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : '';
  };

  const preloadImage = (url: string, index: number) => {
    if (imageCache[url]?.loaded) return;

    const img = new Image();
    img.onload = () => {
      setImageCache(prev => ({
        ...prev,
        [url]: {
          aspectRatio: img.naturalWidth / img.naturalHeight,
          loaded: true
        }
      }));
    };
    img.src = url;
  };

  const nextImage = () => {
    if (!project?.images?.length) return;
    const nextIndex = currentImageIndex === project.images.length - 1 ? 0 : currentImageIndex + 1;
    const nextNextIndex = nextIndex === project.images.length - 1 ? 0 : nextIndex + 1;
    const nextImage = project.images[nextNextIndex];
    if (nextImage && nextImage.media_type === 'image') {
      preloadImage(nextImage.url, nextNextIndex);
    }
    setCurrentImageIndex(nextIndex);
  };

  const previousImage = () => {
    if (!project?.images?.length) return;
    const prevIndex = currentImageIndex === 0 ? project.images.length - 1 : currentImageIndex - 1;
    const prevPrevIndex = prevIndex === 0 ? project.images.length - 1 : prevIndex - 1;
    const prevImage = project.images[prevPrevIndex];
    if (prevImage && prevImage.media_type === 'image') {
      preloadImage(prevImage.url, prevPrevIndex);
    }
    setCurrentImageIndex(prevIndex);
  };

  const currentImage = project?.images?.[currentImageIndex];
  const currentAspectRatio = currentImage?.media_type === 'image' ? imageCache[currentImage.url]?.aspectRatio : null;
  const isImageLoaded = currentImage?.media_type === 'image' ? imageCache[currentImage.url]?.loaded : true;

  const getImageStyle = () => {
    if (!currentAspectRatio) return {};
    
    if (currentAspectRatio >= 0.95 && currentAspectRatio <= 1.05) {
      return {
        width: '80%',
        height: '80%',
        objectFit: 'contain'
      };
    }
    
    if (currentAspectRatio > 1.05) {
      return {
        width: '100%',
        height: 'auto',
        maxHeight: '90%',
        objectFit: 'contain'
      };
    }
    
    return {
      width: 'auto',
      height: '90%',
      maxWidth: '90%',
      objectFit: 'contain'
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-gray-600">Loading project...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-gray-600">Project not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-6 md:p-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            {project.project_name}
          </h1>
          <div className="text-gray-500 mb-6">
            {project.customer?.name}
          </div>

          {project.images?.length > 0 ? (
            <div className="mb-8">
              <div className="relative bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center" style={{ height: '500px' }}>
                {currentImage.media_type === 'youtube' ? (
                  <div className="w-full h-full">
                    <Plyr
                      key={currentImage.id}
                      source={{
                        type: "video",
                        sources: [{
                          src: currentImage.url,
                          provider: "youtube"
                        }]
                      }}
                      options={{
                        controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen']
                      }}
                    />
                  </div>
                ) : currentImage.media_type === 'video' ? (
                  <div className="w-full h-full">
                    <Plyr
                      key={currentImage.id}
                      source={{
                        type: "video",
                        sources: [{ src: currentImage.video_url || '', type: 'video/mp4' }]
                      }}
                      options={{
                        controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen']
                      }}
                    />
                  </div>
                ) : isImageLoaded ? (
                  <img
                    src={currentImage.url}
                    alt={`Project image ${currentImageIndex + 1}`}
                    style={getImageStyle()}
                    className="transition-all duration-200"
                  />
                ) : null}

                {project.images.length > 1 && (
                  <>
                    <button
                      onClick={previousImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                  {currentImageIndex + 1} / {project.images.length}
                </div>
              </div>

              {project.images.length > 1 && (
                <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {project.images.map((image: any, index: number) => (
                    <button
                      key={image.id}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                        currentImageIndex === index
                          ? 'border-indigo-500 opacity-100'
                          : 'border-transparent opacity-60 hover:opacity-100'
                      }`}
                    >
                      {image.media_type === 'youtube' ? (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          <img
                            src={getYouTubeThumbnail(image.url)}
                            alt="YouTube thumbnail"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : image.media_type === 'video' ? (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          <video
                            src={image.video_url}
                            className="w-full h-full object-cover"
                            preload="metadata"
                          />
                        </div>
                      ) : (
                        <img
                          src={image.url}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="mb-8 aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
              <p className="text-gray-500">No images available</p>
            </div>
          )}

          <div className="prose prose-sm md:prose-base max-w-none text-gray-600">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {project.description}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
}